import React, { useState } from 'react';
import AuthForm from './AuthForm';
import AuthField from './AuthField';
import PropTypes from 'prop-types';
import { AuthAPI } from '../../../api';

const DEFAULT_ERROR_MESSAGE = 'An error occurred creating your account.  Please try again later.';

const ActivateForm = ({code, onComplete}) => {
  const [username, setUsername] = useState('');
  const [usernameWarning, setUsernameWarning] = useState();
  const [password, setPassword] = useState('');
  const [passwordWarning, setPasswordWarning] = useState();
  const [password2, setPassword2] = useState('');
  const [password2Warning, setPassword2Warning] = useState();
  const [firstName, setFirstName] = useState('');
  const [firstNameWarning, setFirstNameWarning] = useState();
  const [lastName, setLastName] = useState('');
  const [lastNameWarning, setLastNameWarning] = useState();
  const [contactPhone, setContactPhone] = useState('');
  const [contactPhoneWarning, setContactPhoneWarning] = useState();
  const [activateError, setActivateError] = useState();
  const [busy, setBusy] = useState(false);
  const activate = async () => {
    setActivateError();
    const checks = [
      validateUsername(true),
      validateFirstName(true),
      validateLastName(true),
      validateContactPhone(true),
    ];
    if (!checks.every(v=>v)) return;
    try {
      setBusy(true);
      await AuthAPI.providerAcceptInvitation(
        code,
        username,
        password,
        firstName,
        lastName,
        contactPhone
      );
      setBusy(false);
      onComplete(username);
    } catch (err) {
      setBusy(false);
      console.error(err);  // eslint-disable-line no-console
      const message = (err.body && err.body.message) ? err.body.message : DEFAULT_ERROR_MESSAGE;
      setActivateError(message);
    }
  };

  const validateUsername = (onSubmit=false) => {
    if (username === '' && onSubmit) {
      // only perform this validation if they are actually ready to submit
      setUsernameWarning('Username is required');
      return false;
    }
    setUsernameWarning();
    return true;
  };

  const validatePassword = (onSubmit=false) => {
    if (password === '' && onSubmit) {
      setPasswordWarning('Password is required');
      return false;
    }
    setPasswordWarning();
    return true;
  };


  const validateFirstName = (onSubmit=false) => {
    if (firstName === '' && onSubmit) {
      setFirstNameWarning('First name is required');
      return false;
    }
    setFirstNameWarning();
    return true;
  };

  const validateLastName = (onSubmit=false) => {
    if (lastName === '' && onSubmit) {
      setLastNameWarning('Last name is required');
      return false;
    }
    setLastNameWarning();
    return true;
  };

  const validateContactPhone = (onSubmit=false) => {
    if (contactPhone === '' && onSubmit) {
      setContactPhoneWarning('Contact phone number is required');
      return false;
    }
    setContactPhoneWarning();
    return true;
  };

  const validatePassword2 = (onSubmit=false) => {
    if (onSubmit) {
      if (password2 === '') {
        setPassword2Warning('Please repeat password here to confirm it.');
        return false;
      }
      if (password2 !== password) {
        setPassword2Warning('Passwords do not match');
        return false;
      }
    }
    setPassword2Warning();
    return true;
  };

  const disabled = usernameWarning !== undefined || passwordWarning !== undefined || password2Warning !== undefined;
  return (
    <AuthForm
      buttonLabel="Join Clickability"
      buttonAction={activate}
      disabled={disabled}
      busy={busy}
      error={activateError}
    >

      <AuthField
        id="firstname"
        placeholder="Your first name"
        label="First Name"
        autoComplete="given-name"
        type="text"
        name="firstname"
        value={firstName}
        onChange={setFirstName}
        error={firstNameWarning}
        onBlur={() => validateFirstName()}
      />

      <AuthField
        id="lastname"
        placeholder="Your last name"
        label="Last Name"
        autoComplete="family-name"
        type="text"
        name="lastname"
        value={lastName}
        onChange={setLastName}
        error={lastNameWarning}
        onBlur={() => validateLastName()}
      />

      <AuthField
        id="contactphone"
        placeholder="Contact Telephone Number"
        label="Contact Telephone"
        autoComplete="tel-national"
        type="text"
        name="contactphone"
        value={contactPhone}
        onChange={setContactPhone}
        error={contactPhoneWarning}
        onBlur={() => validateContactPhone()}
      />

      <AuthField
        id="username"
        label="Username"
        type="text"
        name="username"
        value={username}
        onChange={setUsername}
        error={usernameWarning}
        onBlur={() => validateUsername()}
      />

      <AuthField
        id="password"
        label="Password"
        autoComplete="current-password"
        type="password"
        name="password"
        value={password}
        onChange={setPassword}
        error={passwordWarning}
        onBlur={() => validatePassword()}
      />

      <AuthField
        id="password2"
        label="Repeat password"
        autoComplete="current-password"
        type="password"
        name="password2"
        value={password2}
        onChange={setPassword2}
        error={password2Warning}
        onBlur={() => validatePassword2()}
        onEnter={() => !disabled && activate()}
      />

    </AuthForm>
  );
};

ActivateForm.propTypes = {
  code: PropTypes.string.isRequired,
  onComplete: PropTypes.func.isRequired,
  activateApi: PropTypes.func.isRequired,
};

export default ActivateForm;
